import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, Clock3, Play, Trash2 } from 'lucide-react';
import { Button, EmptyState, LoadingState, SectionHeading } from '../components/ui';
import { loadVideoTitles, loadWatchedLectures, saveBookmarkRow, type BookmarkRow } from '../lib/studentUtilities';
import { readLocalProgressEntries } from '../lib/videoProgress';
import type { VideoProgressEntry } from '../lib/videoProgress';

function format(seconds?: number) {
  const s = Math.max(0, Math.floor(seconds ?? 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export function StudentBookmarks() {
  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState<BookmarkRow>();
  const [entries, setEntries] = useState<VideoProgressEntry[]>([]);
  const [titles, setTitles] = useState<Map<string, { VideoName?: string }>>();
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const fetchedRow: BookmarkRow = await loadWatchedLectures();
        if (!active) return;
        setRow(fetchedRow);
        setSynced(Boolean(fetchedRow.objectId));
        // Server entries win per lecture; local entries fill gaps (offline or preview logins).
        const merged = new Map<string, VideoProgressEntry>();
        for (const entry of [...readLocalProgressEntries(), ...(fetchedRow.Bookmarks ?? [])]) {
          if (typeof entry?.id === 'string' && entry.id.trim()) merged.set(entry.id, entry);
        }
        const list = [...merged.values()].sort((a, b) => b.time - a.time);
        setEntries(list);
        const videoTitles = await loadVideoTitles(list.map((entry) => entry.id)).then((map) => new Map<string, { VideoName?: string }>([...map].map(([k, v]) => [k, v as { VideoName?: string }]))).catch(() => new Map<string, { VideoName?: string }>());
        if (active) setTitles(videoTitles);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  const remove = async (videoId: string) => {
    const next = entries.filter((entry) => entry.id !== videoId);
    setEntries(next);
    try { await saveBookmarkRow(row!, next.map(({ id, time, totalTime }) => ({ id, time, totalTime }))); } catch { /* keep local removal */ }
  };

  if (loading) return <LoadingState />;
  return <div className="utility-page">
    <section className="utility-hero"><div className="utility-icon"><Bookmark size={28} /></div><div><h1>Bookmarks</h1><p>{synced ? 'Lectures you have watched, synced to your AyurGuide account.' : 'Watched lectures saved on this device.'}</p></div></section>
    <section className="detail-section">
      <SectionHeading title={`Continue watching · ${entries.length}`} />
      {entries.length ? <div className="subject-list">{entries.map((entry) => {
        const video = titles?.get(entry.id);
        return <div className="subject-row" key={entry.id}>
          <span className="play-circle"><Play size={16} fill="currentColor" /></span>
          <div><strong>{video?.VideoName ?? `Lecture ${entry.id}`}</strong>
            <small><Clock3 size={12} /> Paused at {format(entry.time)} of {format(entry.totalTime)}</small></div>
          <div className="row-actions">
            <Link to={`/video/${entry.id}`}><Button variant="soft">Resume</Button></Link>
            <button className="icon-danger" aria-label="Remove" onClick={() => void remove(entry.id)}><Trash2 size={15} /></button>
          </div>
        </div>;
      })}</div>
      : <EmptyState icon={Bookmark} title="Nothing saved yet" description="Lectures you start watching appear here so you can pick up exactly where you paused." action={<Link to="/courses"><Button variant="soft">Browse courses</Button></Link>} />}
    </section>
  </div>;
}
